import React, { ReactChild } from 'react';
import clsx from 'clsx';
import { Button, createStyles, Dialog, Grid, GridJustification, makeStyles, Theme, Typography } from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Variant } from '@material-ui/core/styles/createTypography';

interface IBasicDialog {
    title: string,
    description?: string,
    handleFirstButton?: () => void,
    handleSecondButton?: () => void,
    textFirstButton?: string,
    textSecondButton?: string,
    open: boolean,
    children?: ReactChild,    
    cssProperties?: ICssProperties,
    reverseButtons?: boolean,
    customButtons?: ReactChild,
}

interface ICssProperties {
    minWidth?: string,
    maxWidth?: string,
    padding?: string,
    colorFirstButton?: string,
    justifyButtons?: GridJustification,
    titleVariant?: Variant,
    descriptionVariant?: Variant,
}

const useStyles = makeStyles((theme: Theme) => createStyles({    
    root: (props: ICssProperties) => ({
        maxWidth: props.maxWidth || '400px',
        minWidth: props.minWidth || '250px',
        padding: props.padding || '0.5rem'
    }),
    firstButton: (props: ICssProperties) => ({
        color: props.colorFirstButton || theme.palette.error.main,
        borderColor: props.colorFirstButton || theme.palette.error.main
    }),
    titleSpacing: {
        paddingBottom: '0.75rem',
    },
    fullWidth: {
        width: '100%'
    },
    content: {
        overflowY: 'hidden',
        paddingBottom: '1rem'
    },
    dialogActionPadding: {
        padding: '0.5rem 1.5rem 1rem 1.5rem' 
    }
}));

export function BasicConfirmDialog({
    title, 
    description, 
    handleFirstButton, 
    handleSecondButton, 
    textFirstButton, 
    textSecondButton, 
    open, 
    children, 
    cssProperties, 
    reverseButtons, 
    customButtons}: IBasicDialog) {
    const classes = useStyles(cssProperties || {});

    if (!cssProperties) {
        cssProperties = {
            justifyButtons: 'flex-start', 
            titleVariant: 'subtitle2',
            descriptionVariant: 'body2'
        };
    }
    else {
        cssProperties = {
            justifyButtons: cssProperties.justifyButtons || 'flex-start',
            titleVariant: cssProperties.titleVariant || 'subtitle2',
            descriptionVariant: cssProperties.descriptionVariant || 'body2'
        };
    }

    return (
        <Dialog 
            maxWidth='lg'
            aria-labelledby="simple-dialog-title" 
            open={open}>
            <div className={classes.root}>
                <DialogTitle disableTypography>
                    <Typography variant={cssProperties?.titleVariant} className={classes.titleSpacing}>{title}</Typography>
                    {description && <Typography variant={cssProperties?.descriptionVariant}>{description}</Typography>}
                </DialogTitle>
                {children &&
                    <DialogContent className={classes.content}>
                        {children}
                    </DialogContent>
                }
                {!customButtons && (handleFirstButton || handleSecondButton) && 
                    <DialogActions className={classes.dialogActionPadding}>
                        <Grid 
                            container
                            spacing={2}
                            direction={reverseButtons ? 'row-reverse' : 'row'}
                            justify={cssProperties?.justifyButtons}
                        >
                            {handleFirstButton &&
                                <Grid item>
                                    <Button 
                                        onClick={handleFirstButton} 
                                        variant="outlined"
                                        size="large"
                                        className={clsx(classes.firstButton, {
                                            [classes.fullWidth]: !handleSecondButton
                                        })}>
                                        {textFirstButton}
                                    </Button>
                                </Grid>
                            }
                            {handleSecondButton &&
                                <Grid item>
                                    <Button 
                                        onClick={handleSecondButton} 
                                        variant="contained" 
                                        color="primary"
                                        size="large"
                                        disableElevation
                                        className={clsx({
                                            [classes.fullWidth]: !handleFirstButton
                                        })}>
                                        {textSecondButton}
                                    </Button>
                                </Grid>
                            }
                        </Grid>
                    </DialogActions>
                }
                {customButtons && 
                    <DialogActions className={classes.dialogActionPadding}>
                        {customButtons}
                    </DialogActions>}
            </div>
        </Dialog>
    );
}